import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Send, ArrowLeft, User, Bot } from "lucide-react";
import { useRequireAuth } from '@/hooks/use-auth-hooks';
import { useApiService } from '@/lib/api';
import { useBeaconCache } from '@/hooks/use-beacon-cache';
import { useConversationCache } from '@/hooks/use-conversation-cache';
import { AceRenderer } from '@/components/ui/ace-renderer';

// Enhanced chat message component with LaTeX and markdown support
const ChatMarkdown = ({ content, isUser = false }: { content: string; isUser?: boolean }) => {
  return (
    <div className={`text-sm ${isUser ? 'text-primary-foreground' : ''}`}>
      <AceRenderer 
        content={content}
        compact={true}
        enableMath={true}
        className={isUser ? 'text-primary-foreground' : ''}
      />
    </div>
  );
};

interface Beacon {
  id: string;
  type: string;
  name: string;
  subject_id: string;
  chapter_id: string;
  definition?: string;
  description?: string;
  keywords?: string[];
}

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
}

export function BeaconChatPage() {
  const { user } = useRequireAuth();
  const { beaconId, chatId } = useParams<{ beaconId: string; chatId?: string }>();
  const navigate = useNavigate();
  const apiService = useApiService();
  
  // Use beacon cache for optimized loading
  const {
    isLoadingBeacon,
    isLoadingChat,
    error: cacheError,
    fetchBeaconData,
    getCachedBeacon
  } = useBeaconCache();

  // Use conversation cache for real-time updates
  const {
    refreshConversations
  } = useConversationCache();
  
  const [beacon, setBeacon] = useState<Beacon | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAiUnavailable, setIsAiUnavailable] = useState(false);
  const [existingChatId, setExistingChatId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasInitialized = useRef(false);

  // Generate static welcome message
  const generateWelcomeMessage = (beaconData: Beacon): string => {
    const typeText = beaconData.type ? beaconData.type.toLowerCase() : 'concept';
    
    return `👋 **Welcome to your learning session!**

Ready to dive into **${beaconData.name}**? I'm here to guide you through this ${typeText} with clear explanations, practical examples, and personalized support.

✨ **What we can explore together:**
• Core concepts and definitions
• Real-world applications
• Practice questions and examples
• Connected topics and relationships

💡 Feel free to ask questions, request explanations, or use the suggested prompts below to get started. Let's make learning engaging and effective!`;
  };

  // Convert chat history to messages format
  const convertHistoryToMessages = (history: Array<{ role: string; message: string; timestamp: string }>): Message[] => {
    return history.map((item, index) => ({
      id: `history-${index}`,
      content: item.message,
      sender: item.role === 'user' ? 'user' : 'assistant',
      timestamp: new Date(item.timestamp)
    }));
  };

  // Load beacon data from API and check for existing chat
  useEffect(() => {
    const loadBeaconData = async () => {
      if (!beaconId) {
        navigate('/beacons');
        return;
      }

      // Reset state when switching beacons
      if (beacon?.id && beacon.id !== beaconId) {
        setBeacon(null);
        setMessages([]);
        setExistingChatId(null);
        setIsAiUnavailable(false);
        hasInitialized.current = false;
      }

      // Prevent multiple initializations for the same beacon
      if (hasInitialized.current && beacon?.id === beaconId) {
        return;
      }

      hasInitialized.current = true; // Set early to prevent duplicates
      
      try {
        // If we have a specific chat ID from the URL, load that specific chat
        if (chatId) {
          const chatResponse = await apiService.get<{
            _id: string;
            title: string;
            userId: string;
            entityId: string;
            subjectId?: string;
            history: Array<{
              role: string;
              message: string;
              timestamp: string;
            }>;
            createdAt: string;
            updatedAt: string;
            isDeleted: boolean;
            isFavorite: boolean;
          }>(`/chats/${chatId}`);

          // Also fetch beacon data
          const { beacon: beaconData } = await fetchBeaconData(beaconId);
          
          if (!beaconData) {
            hasInitialized.current = false;
            navigate('/beacons');
            return;
          }

          // Transform beacon data
          const transformedBeacon: Beacon = {
            id: beaconData.id,
            type: beaconData.type || 'concept',
            name: beaconData.name || 'Unknown',
            subject_id: beaconData.subject_id || '',
            chapter_id: beaconData.class_id || '',
            definition: beaconData.description || '',
            description: beaconData.description || '',
            keywords: []
          };

          setBeacon(transformedBeacon);
          setExistingChatId(chatResponse._id);

          if (chatResponse.history && chatResponse.history.length > 0) {
            const chatMessages = convertHistoryToMessages(chatResponse.history);
            setMessages(chatMessages);
          } else {
            // Empty history - show welcome message
            const welcomeContent = generateWelcomeMessage(transformedBeacon);
            setMessages([{
              id: '1',
              content: welcomeContent,
              sender: 'assistant',
              timestamp: new Date()
            }]);
          }
        } else {
          // No specific chat ID, load beacon and check for existing chat
          const { beacon: beaconData, chat: existingChat } = await fetchBeaconData(beaconId);
          
          if (!beaconData) {
            hasInitialized.current = false; // Reset on error
            navigate('/beacons');
            return;
          }

          // Transform beacon data to match local interface
          const transformedBeacon: Beacon = {
            id: beaconData.id,
            type: beaconData.type || 'concept',
            name: beaconData.name || 'Unknown',
            subject_id: beaconData.subject_id || '',
            chapter_id: beaconData.class_id || '',
            definition: beaconData.description || '',
            description: beaconData.description || '',
            keywords: []
          };

          setBeacon(transformedBeacon);
          
          if (existingChat && existingChat._id) {
            // We found an existing chat, but need to fetch its full content
            // since we don't cache chat history - load it fresh
            setExistingChatId(existingChat._id);
            try {
              const fullChatResponse = await apiService.get<{
                _id: string;
                history: Array<{
                  role: string;
                  message: string;
                  timestamp: string;
                }>;
              }>(`/chats/${existingChat._id}`);
              
              if (fullChatResponse.history && fullChatResponse.history.length > 0) {
                const chatMessages = convertHistoryToMessages(fullChatResponse.history);
                setMessages(chatMessages);
              } else {
                // Empty history - show welcome message
                const welcomeContent = generateWelcomeMessage(transformedBeacon);
                setMessages([{
                  id: '1',
                  content: welcomeContent,
                  sender: 'assistant',
                  timestamp: new Date()
                }]);
              }
            } catch (error) {
              // Failed to load chat history, show welcome message
              const welcomeContent = generateWelcomeMessage(transformedBeacon);
              setMessages([{
                id: '1',
                content: welcomeContent,
                sender: 'assistant',
                timestamp: new Date()
              }]);
            }
          } else {
            // No existing chat, start with welcome message
            const welcomeContent = generateWelcomeMessage(transformedBeacon);
            setMessages([{
              id: '1',
              content: welcomeContent,
              sender: 'assistant',
              timestamp: new Date()
            }]);
          }
        }
      } catch (error) {
        hasInitialized.current = false; // Reset on error
        navigate('/beacons');
      }
    };

    loadBeaconData();
  }, [beaconId, chatId, fetchBeaconData, navigate]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || !beacon) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue.trim(),
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = '24px';
    }

    try {
      // Validate required data for API call
      if (!beacon.subject_id || !beacon.id) {
        console.warn('Missing required beacon data for AI API call, using fallback');
        throw new Error('Missing beacon data');
      }

      // Prepare request payload - include existing chat ID if available
      const requestPayload: any = {
        prompt: userMessage.content,
        subjectID: beacon.subject_id,
        entityID: beacon.id
      };

      // Include existing chat ID to continue the conversation
      if (existingChatId) {
        requestPayload.id = existingChatId;
      } else {
        // Starting new chat
      }

      // Call AI API endpoint using SSE (Server-Sent Events) method
      const response = await apiService.postSSE<{
        success: boolean;
        data: {
          response: string;
          chatId?: string;
        };
        message?: string;
      }>('/agents/beacons/prompt', requestPayload);

      if (response.success && response.data?.response) {
        // If we got a new chat ID from the response, store it and refresh conversations
        if (response.data.chatId && !existingChatId) {
          setExistingChatId(response.data.chatId);
          // Refresh conversations to include new beacon chat in history with a larger delay
          setTimeout(async () => {
            try {
              await refreshConversations();
            } catch (err) {
              console.warn('Failed to refresh conversations after new beacon chat creation:', err);
            }
          }, 2000); // 2 second delay to ensure backend has processed the chat
        }

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: response.data.response,
          sender: 'assistant',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
        setIsAiUnavailable(false); // Reset AI availability flag
      } else {
        setIsAiUnavailable(true);
        
        // Fallback to local response if API fails
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: generateResponse(userMessage.content, beacon),
          sender: 'assistant',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      setIsAiUnavailable(true);
      
      // Fallback to local response on error
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: `${generateResponse(userMessage.content, beacon)} 

*Note: AI assistant is temporarily unavailable. This is a basic response.*`,
        sender: 'assistant',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const generateResponse = (question: string, beacon: Beacon | null): string => {
    if (!beacon) return "I'm sorry, I don't have information about this beacon.";
    
    const lowerQuestion = question.toLowerCase();
    
    if (lowerQuestion.includes('what is') || lowerQuestion.includes('define')) {
      return beacon.definition || beacon.description || `${beacon.name} is a ${beacon.type} in our knowledge base.`;
    }
    
    if (lowerQuestion.includes('explain') || lowerQuestion.includes('tell me about')) {
      return `${beacon.name} is ${beacon.description || beacon.definition || `an important ${beacon.type} concept`}. ${beacon.keywords ? `Key related terms include: ${beacon.keywords.join(', ')}.` : ''}`;
    }
    
    if (lowerQuestion.includes('keywords') || lowerQuestion.includes('related terms')) {
      return beacon.keywords ? `The key terms related to ${beacon.name} are: ${beacon.keywords.join(', ')}.` : `I don't have specific keywords for ${beacon.name} at the moment.`;
    }
    
    if (lowerQuestion.includes('example') || lowerQuestion.includes('examples')) {
      return `Here are some ways to understand ${beacon.name}: ${beacon.description || beacon.definition || 'This concept is important in understanding the broader topic.'}`;
    }
    
    // Default response
    return `That's a great question about ${beacon.name}! ${beacon.description || beacon.definition || `This ${beacon.type} is an important concept.`} Is there a specific aspect you'd like to explore further?`;
  };

  const suggestedPrompts = beacon ? [
    `What is ${beacon.name}?`,
    `Explain ${beacon.name} in simple terms`,
    `Give me examples of ${beacon.name}`,
    `How does ${beacon.name} relate to other concepts?`,
    ].filter(Boolean) : [];

  const handlePromptClick = async (prompt: string) => {
    if (isLoading || !beacon) return;

    // Create user message immediately
    const userMessage: Message = {
      id: Date.now().toString(),
      content: prompt,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue(''); // Clear input
    setIsLoading(true);

    try {
      // Validate required data for API call
      if (!beacon.subject_id || !beacon.id) {
        console.warn('Missing required beacon data for AI API call, using fallback');
        throw new Error('Missing beacon data');
      }

      // Prepare request payload - include existing chat ID if available
      const requestPayload: any = {
        prompt: prompt,
        subjectID: beacon.subject_id,
        entityID: beacon.id
      };

      // Include existing chat ID to continue the conversation
      if (existingChatId) {
        requestPayload.id = existingChatId;
      } else {
        // Starting new chat with suggested prompt
      }

      // Call AI API endpoint using SSE (Server-Sent Events) method
      const response = await apiService.postSSE<{
        success: boolean;
        data: {
          response: string;
          chatId?: string;
        };
        message?: string;
      }>('/agents/beacons/prompt', requestPayload);

      if (response.success && response.data?.response) {
        // If we got a new chat ID from the response, store it and refresh conversations
        if (response.data.chatId && !existingChatId) {
          setExistingChatId(response.data.chatId);
          // Refresh conversations to include new beacon chat in history with a larger delay
          setTimeout(async () => {
            try {
              await refreshConversations();
            } catch (err) {
              console.warn('Failed to refresh conversations after new beacon chat creation:', err);
            }
          }, 2000); // 2 second delay to ensure backend has processed the chat
        }

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: response.data.response,
          sender: 'assistant',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
        setIsAiUnavailable(false); // Reset AI availability flag
      } else {
        console.warn('AI API returned unsuccessful response, using fallback');
        setIsAiUnavailable(true);
        
        // Fallback to local response if API fails
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: generateResponse(prompt, beacon),
          sender: 'assistant',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('Error sending message to AI:', error);
      setIsAiUnavailable(true);
      
      // Fallback to local response on error
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: `${generateResponse(prompt, beacon)} 

*Note: AI assistant is temporarily unavailable. This is a basic response.*`,
        sender: 'assistant',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackNavigation = () => {
    // Simple same-tab navigation, go back to beacons browser
    navigate('/beacons');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    
    // Auto-resize textarea
    const target = e.target;
    target.style.height = 'auto';
    target.style.height = Math.min(target.scrollHeight, 128) + 'px';
  };

  if (!user) {
    return null;
  }

  // Show loading screen while beacon data is being loaded
  if (isLoadingBeacon) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg font-medium">Loading beacon data...</p>
          <p className="text-sm text-muted-foreground">Setting up your learning session</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex-shrink-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
        <div className="flex items-center gap-4 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBackNavigation}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          
          {beacon && (
            <div className="flex items-center gap-3 flex-1">
              <div>
                <h1 className="text-xl font-semibold">{beacon.name}</h1>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="capitalize">
                    {beacon.type}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {existingChatId ? 'Continuing Chat' : 'Learning Chat'}
                  </span>
                  {isAiUnavailable && (
                    <Badge variant="secondary" className="text-xs">
                      Basic Mode
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Messages - Scrollable area */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="p-4 space-y-4 min-h-full">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.sender === 'assistant' && (
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                    <Bot className="h-4 w-4 text-primary-foreground" />
                  </div>
                </div>
              )}
              
              <Card className={`max-w-[80%] ${message.sender === 'user' ? 'bg-primary text-primary-foreground' : ''}`}>
                <CardContent className="p-3">
                  <ChatMarkdown content={message.content} isUser={message.sender === 'user'} />
                  <span className="text-xs opacity-70 mt-2 block">
                    {message.timestamp.toLocaleTimeString()}
                  </span>
                </CardContent>
              </Card>

              {message.sender === 'user' && (
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                    <User className="h-4 w-4" />
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex gap-3 justify-start">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <Bot className="h-4 w-4 text-primary-foreground" />
                </div>
              </div>
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {isAiUnavailable ? 'Processing...' : 'AI is thinking...'}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Suggested Prompts - Inside scrollable area */}
        {messages.length <= 1 && suggestedPrompts.length > 0 && (
          <div className="px-4 pb-2">
            <p className="text-sm text-muted-foreground mb-2">Suggested questions:</p>
            <div className="flex flex-wrap gap-2">
              {suggestedPrompts.map((prompt, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => handlePromptClick(prompt)}
                >
                  {prompt}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Input - Fixed at bottom */}
      <div className="flex-shrink-0 p-4 bg-background border-t">
        <div className="relative max-w-4xl mx-auto">
          <div className="relative flex items-end gap-2 p-3 rounded-3xl border border-border bg-background shadow-lg hover:shadow-xl transition-all duration-200 focus-within:shadow-xl focus-within:border-ring/50 focus-within:ring-1 focus-within:ring-ring/20">
            <Textarea
              ref={textareaRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder={`Ask me anything about ${beacon?.name || 'this topic'}... 💡`}
              className="flex-1 min-h-[24px] max-h-32 resize-none border-0 bg-transparent p-0 text-sm placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-muted"
              rows={1}
              style={{
                height: 'auto',
                minHeight: '24px',
                maxHeight: '128px',
                overflow: 'hidden'
              }}
            />
            <Button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              size="icon"
              className="h-8 w-8 rounded-full bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex-shrink-0 shadow-md hover:shadow-lg"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent" />
              ) : (
                <Send className="h-3 w-3" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground/70 mt-2 text-center">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
}
